/* eslint-disable max-lines */

import { ComputedDatum, ResponsivePie } from '@nivo/pie';
import { clearTree, fetchUserProfileAggs, requestTreeFiltersToogle } from 'actions';
import { allowedReadOnlyDimensions, metricsLinksMap } from 'constants/dashboard';
import { FULL_DATE_FORMAT } from 'constants/formats';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import withWidget from 'decorators/widget';
import WidgetLoading from 'decorators/widget/WidgetLoading';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomerDomain, getUser, getUserCustomer } from 'selectors/auth';
import { getNavParamsByResource, getNavWidgetFilters } from 'selectors/nav';
import { getPermissionsPolicy } from 'selectors/permissions';
import { getFetchUserProfileAggsLoading } from 'selectors/users';
import { useSelector } from 'store';
import { DefaultWidgetComponentProps, NivoBarData } from 'types';
import logEvent from 'utils/analytics';
import { buildPieHistogramDataFromMetrics } from 'utils/dashboard';
import { getValuesFromDimensions, setNewValue, transformToString } from 'utils/parserTree';

import { isActionAuthorized } from 'utils/permissions';
import { FINAL_AFTER_HOURS_HOUR, INITIAL_AFTER_HOURS_HOUR, kFormatter } from 'utils/timeRanges';
import { useHistory } from 'utils/urls';

const SmallPieMetricWidget: React.FC<DefaultWidgetComponentProps> = (props) => {
  const { widgetId, widgetData, queryData } = props;

  const history = useHistory();
  const dispatch = useDispatch();
  const customerDomain = useSelector(getCustomerDomain);
  const customer = useSelector(getUserCustomer);
  const widgetFilters = useSelector(getNavWidgetFilters(widgetId));
  const user = useSelector(getUser);
  const aggsLoading = useSelector(getFetchUserProfileAggsLoading);

  const filters = useSelector(getNavParamsByResource(resourceQueryParamName.metrics));
  const policy = useSelector(getPermissionsPolicy);

  // const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (user) {
      dispatch(fetchUserProfileAggs({ userId: user.uuid }));
    }
  }, [dispatch, user]);

  if (widgetData == null || queryData == null || aggsLoading) {
    return <WidgetLoading />;
  }

  const processedData = buildPieHistogramDataFromMetrics(widgetData);

  const handleClick = (datum: ComputedDatum<NivoBarData>): void => {
    const [id, type] = widgetId.split('--');
    logEvent(`Dashboard-click-${id}-${type}`);

    // this widget only ever has a single metric config
    // so we pluck the first query config
    const query = queryData.queries[0];
    if (query == null) {
      return;
    }

    const info = metricsLinksMap[query.metric];
    if (info == null || !isActionAuthorized(info.action, user.roles, policy)) {
      return;
    }

    const series = widgetData.results[0];

    if (series == null) {
      return;
    }

    const dimensions: Record<string, string | string[]> = {};
    const infoDimension = info.dimensions;
    if (infoDimension != null) {
      Object.entries(infoDimension).forEach(([dimension, param]) => {
        const allowed = allowedReadOnlyDimensions[query.metric];

        // if allowedDimension is not defined or the dimension is not included,
        // just ignore it

        if (allowed == null || !allowed.includes(dimension)) {
          return;
        }

        let value: string | string[] = '';
        if (filters[dimension] != null) {
          const topValue = filters[dimension];
          value = topValue;
        }

        if (widgetFilters[dimension] != null) {
          const widgetValue = filters[dimension];
          value = widgetValue;
        }

        if (query.group_by === dimension) {
          // @ts-ignore
          value = 'formattedValue' in datum ? datum.data.labelValue : datum.data.data.labelValue;
        }

        if (query.metric === 'after_hours' && dimension === 'hour_range') {
          const zone = customer?.config.dashboard_config?.primary_timezone || '';
          let initialHour = INITIAL_AFTER_HOURS_HOUR;
          let finalHour = FINAL_AFTER_HOURS_HOUR;

          let offset = 0;
          if (zone) {
            offset = moment.tz(zone).utcOffset() / 60;
          } else {
            offset = moment.tz().utcOffset() / 60;
          }
          initialHour = (initialHour + offset) % 24;
          finalHour = (finalHour + offset) % 24;

          value = `${initialHour},${finalHour}`;
        }

        dimensions[`${info.resource}__${param}`] = value;
      });

      // Metric level dimensions
      if (query.dimensions != null) {
        query.dimensions.forEach((dimension) => {
          const allowed = allowedReadOnlyDimensions[query.metric];

          // if allowedDimension is not defined or the dimension is not included,
          // just ignore it
          if (allowed == null || !allowed.includes(dimension.name)) {
            return;
          }

          if (!dimensions[`${info.resource}__${infoDimension[dimension.name]}`]) {
            dimensions[`${info.resource}__${infoDimension[dimension.name]}`] = dimension.value;
          }
        });
      }
    }

    const start = moment.utc(queryData.start_time * 1000).format(FULL_DATE_FORMAT);
    const end = moment.utc(queryData.end_time * 1000).format(FULL_DATE_FORMAT);

    const tree = getValuesFromDimensions(dimensions);

    const newTree = setNewValue(tree, 'date_range', `${start}<>${end}`, '', true);

    dispatch(requestTreeFiltersToogle({ value: true }));
    dispatch(clearTree());
    history.pushLookup({
      customerDomain,
      routeName: info.route,
      queryParams: {
        [`${info.resource}__filters_search`]: transformToString(newTree),
        [`${info.resource}__has_events`]: ['true', 'false'],
        ...dimensions,
      },
    });
  };

  const handleMessagesClick = (): void => {
    const [id, type] = widgetId.split('--');
    logEvent(`Dashboard-click-${id}-${type}`);

    // this widget only ever has a single metric config
    // so we pluck the first query config
    const query = queryData.queries[0];
    if (query == null) {
      return;
    }

    const info = metricsLinksMap[query.metric];
    if (info == null || !isActionAuthorized(info.action, user.roles, policy)) {
      return;
    }

    const series = widgetData.results[0];

    if (series == null) {
      return;
    }

    const start = moment.utc(queryData.start_time * 1000).format(FULL_DATE_FORMAT);
    const end = moment.utc(queryData.end_time * 1000).format(FULL_DATE_FORMAT);

    const dimensions: Record<string, string | string[]> = {};
    if (info.dimensions != null) {
      Object.entries(info.dimensions).forEach(([dimension, param]) => {
        const allowed = allowedReadOnlyDimensions[query.metric];

        // if allowedDimension is not defined or the dimension is not included,
        // just ignore it
        if (allowed == null || !allowed.includes(dimension)) {
          return;
        }

        const value: string[] = [];

        if (queryData.queries[0].dimensions) {
          queryData.queries[0].dimensions.forEach((dim) => {
            if (dim.name === dimension) value.push(dim.value);
          });
        }

        dimensions[`${info.resource}__${param}`] = value;
      });
    }
    if (query.metric === 'after_hours') {
      const zone = customer?.config.dashboard_config?.primary_timezone || '';
      let initialHour = INITIAL_AFTER_HOURS_HOUR;
      let finalHour = FINAL_AFTER_HOURS_HOUR;

      let offset = 0;
      if (zone) {
        offset = moment.tz(zone).utcOffset() / 60;
      } else {
        offset = moment.tz().utcOffset() / 60;
      }
      initialHour = (initialHour + offset) % 24;
      finalHour = (finalHour + offset) % 24;

      const value = `${initialHour},${finalHour}`;
      dimensions[`${info.resource}__hour_range`] = value;
    }

    const tree = getValuesFromDimensions(dimensions);

    const newTree = setNewValue(tree, 'date_range', `${start}<>${end}`, '', true);

    dispatch(
      requestTreeFiltersToogle({
        value: true,
      })
    );
    dispatch(clearTree());

    history.pushLookup({
      customerDomain,
      routeName: info.route,
      queryParams: {
        [`${info.resource}__filters_search`]: transformToString(newTree),
        [`${info.resource}__has_events`]: ['true'],
      },
    });
  };

  return (
    <div
      ref={(node): void => {
        setWrapperRef(node);
      }}
      className="-mt-1 w-full h-full flex flex-row items-start justify-between gap-4 overflow-visible chart-svg-overflow"
    >
      <div className="flex-1">
        <button
          type="button"
          className="text-body whitespace-no-wrap"
          onClick={handleMessagesClick}
        >
          <span className="text-litlingo-primary-120 underline hover:cursor-pointer">
            {
              // @ts-ignore
              `${kFormatter(widgetData.results[0].total)} Messages`
            }
          </span>
        </button>
      </div>
      <div className="h-full w-20 flex flex-col self-end overflow-visible chart-svg-overflow">
        {wrapperRef && (
          <ResponsivePie<NivoBarData>
            data={processedData.data}
            theme={{
              tooltip: {
                container: {
                  background: 'none',
                  boxShadow: 'none',
                },
              },
              labels: {
                text: {
                  fontWeight: 'bold',
                },
              },
            }}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            legends={[
              {
                anchor: 'bottom-left',
                direction: 'column',
                itemWidth: 100,
                itemHeight: 20,
                translateX: wrapperRef ? -wrapperRef.getBoundingClientRect().width + 80 : 0,
                translateY: -10,

                // @ts-ignore
                onClick: handleClick,
              },
            ]}
            tooltip={(d): JSX.Element => (
              <div className="bg-litlingo-off-white p-1 rounded flex items-center shadow-md z-100">
                <div
                  style={{
                    background: d.datum.color,
                  }}
                  className="h-3 w-3 mr-2"
                />
                <span className="whitespace-no-wrap text">
                  {d.datum.label}: <b>{d.datum.value}</b>
                </span>
              </div>
            )}
            onClick={handleClick}
            colors={['#FFC700', '#37A47D', '#858585']}
            startAngle={90}
            endAngle={450}
            animate={false}
          />
        )}
      </div>
    </div>
  );
};

export default withWidget({
  minWidth: 4,
  minHeight: 6,
})(SmallPieMetricWidget);
